import { Injectable, signal } from '@angular/core';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';

@Injectable()
export class ContractsDetailUiStore {
  private readonly expandedContractIds = signal<Set<UUID>>(new Set());

  // Contracts that have already had the active/inactive default applied (or been touched by
  // expandAll/collapseAll). Needed because expandedContractIds alone can't tell "never seen" apart
  // from "manually collapsed" - both look like "not in the set".
  private readonly seenContractIds = signal<Set<UUID>>(new Set());

  private readonly showInactiveContractsState = signal<boolean>(false);

  private readonly showDeletedLineItemsState = signal<boolean>(false);

  // The organization the reveal flags currently apply to. Held here rather than in the facade so the
  // facade stays stateless, and so a reload for the same organization can be told apart from a switch.
  private readonly scopedOrganizationId = signal<UUID | undefined>(undefined);

  /** Whether contracts that are not active today are included in the list. */
  public readonly showInactiveContracts = this.showInactiveContractsState.asReadonly();

  /** Whether soft-deleted line items are included in each contract's line item table. */
  public readonly showDeletedLineItems = this.showDeletedLineItemsState.asReadonly();

  /** The organization the currently loaded data and reveal flags belong to. */
  public readonly organizationId = this.scopedOrganizationId.asReadonly();

  /**
   * Checks whether a contract panel is expanded.
   *
   * @param {UUID} contractId - The contract ID to check.
   * @returns {boolean} True if the contract panel is expanded.
   */
  public isExpanded(contractId: UUID): boolean {
    return this.expandedContractIds().has(contractId);
  }

  /**
   * Sets the expanded state for a single contract panel.
   *
   * @param {UUID} contractId - The contract ID.
   * @param {boolean} isExpanded - Whether the panel should be expanded.
   * @returns {void} This method does not return a value.
   */
  public setExpanded(contractId: UUID, isExpanded: boolean): void {
    this.expandedContractIds.update((ids) => {
      const newIds = new Set(ids);
      if (isExpanded) {
        newIds.add(contractId);
      } else {
        newIds.delete(contractId);
      }
      return newIds;
    });
  }

  /**
   * Applies the active/inactive default expand state to any contract not seen before, and leaves the
   * expand state of every already-seen contract untouched. Called after every fetch so a reveal-toggle
   * refetch brings newly-visible contracts in with the right default without undoing a user's manual
   * expand/collapse of a contract already on screen.
   *
   * @param {{ id: UUID; isActive: boolean }[]} contracts - Every contract from the latest fetch.
   * @returns {void} This method does not return a value.
   */
  public applyDefaultExpansion(contracts: { id: UUID; isActive: boolean }[]): void {
    const seen = this.seenContractIds();
    const unseen = contracts.filter((contract) => !seen.has(contract.id));
    if (unseen.length === 0) {
      return;
    }

    this.expandedContractIds.update((ids) => {
      const newIds = new Set(ids);
      for (const contract of unseen) {
        if (contract.isActive) {
          newIds.add(contract.id);
        } else {
          newIds.delete(contract.id);
        }
      }
      return newIds;
    });
    this.seenContractIds.update((ids) => new Set([...ids, ...unseen.map((contract) => contract.id)]));
  }

  /**
   * Force-expands every given contract, regardless of its current state, and marks all of them as seen so
   * a later {@link applyDefaultExpansion} does not re-collapse an inactive contract expanded here.
   *
   * @param {UUID[]} contractIds - The contract IDs to expand.
   * @returns {void} This method does not return a value.
   */
  public expandAll(contractIds: UUID[]): void {
    this.expandedContractIds.update((ids) => new Set([...ids, ...contractIds]));
    this.seenContractIds.update((ids) => new Set([...ids, ...contractIds]));
  }

  /**
   * Force-collapses every given contract, regardless of its current state, and marks all of them as seen
   * so a later {@link applyDefaultExpansion} does not re-expand an active contract collapsed here.
   *
   * @param {UUID[]} contractIds - The contract IDs to collapse.
   * @returns {void} This method does not return a value.
   */
  public collapseAll(contractIds: UUID[]): void {
    this.expandedContractIds.update((ids) => {
      const newIds = new Set(ids);
      for (const contractId of contractIds) {
        newIds.delete(contractId);
      }
      return newIds;
    });
    this.seenContractIds.update((ids) => new Set([...ids, ...contractIds]));
  }

  /**
   * Sets whether contracts that are not active today are included.
   *
   * @param {boolean} shouldShow - Whether to include inactive contracts.
   * @returns {void} This method does not return a value.
   */
  public setShowInactiveContracts(shouldShow: boolean): void {
    this.showInactiveContractsState.set(shouldShow);
  }

  /**
   * Sets whether soft-deleted line items are included.
   *
   * @param {boolean} shouldShow - Whether to include soft-deleted line items.
   * @returns {void} This method does not return a value.
   */
  public setShowDeletedLineItems(shouldShow: boolean): void {
    this.showDeletedLineItemsState.set(shouldShow);
  }

  /**
   * Scopes the reveal flags to an organization. Switching to a different organization resets both flags to
   * off, so history and deleted line items are never revealed implicitly; re-loading the same organization
   * (a toggle or a Salesforce sync refresh) keeps them.
   *
   * @param {UUID | undefined} organizationId - The organization the tab is now showing.
   * @returns {void} This method does not return a value.
   */
  public scopeToOrganization(organizationId: UUID | undefined): void {
    if (this.scopedOrganizationId() === organizationId) {
      return;
    }

    this.scopedOrganizationId.set(organizationId);
    this.showInactiveContractsState.set(false);
    this.showDeletedLineItemsState.set(false);
  }

  /**
   * Clears all expand/collapse UI state, including which contracts have already had a default
   * applied, so the next fetch for a new organization defaults all of its contracts afresh. The
   * reveal flags are scoped to the organization ({@link scopeToOrganization}) rather than to a load,
   * so they deliberately survive this.
   *
   * @returns {void} This method does not return a value.
   */
  public clearAll(): void {
    this.expandedContractIds.set(new Set());
    this.seenContractIds.set(new Set());
  }
}
